/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HeaderModule
 */

import { Component, Inject } from '@angular/core';

import {
    combineLatest,
    from,
    throwError,
} from 'rxjs';

import {
    catchError,
    map,
    switchMap,
    take,
} from 'rxjs/operators';

import { IPromise } from 'angular';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { IUserActivity, LicenseTierType } from 'generated-types';
import { StringService } from 'string-service';
import { selectUsername } from 'ng/root-store/user-profile/user-profile.selectors';
import { selectDefaultLicenseTier } from 'ng/root-store/system-info/system-info.selectors';
import * as UserAccount from 'ng/root-store/user-account/user-account.selectors';
import { DevLoggerService } from 'ng/modules/core/services';

import {
    selectControllerTimeDifference,
} from 'ng/root-store/controller-initial-data-store/controller-initial-data.selectors';

import { CportalService } from 'ajs/modules/cportal';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { TUserItem, UserItem } from 'ajs/modules/accounts/factories/user/user.item.factory';

const PORTAL_URL_VMW_MODE = '/login?authmode=vmw';

/**
 * @description
 *     Container component for AviMainHeaderUserMenuComponent. Fetches data to pass as bindings to
 *     AviMainHeaderUserMenuComponent.
 * @author alextsg
 */
@Component({
    selector: 'avi-main-header-user-menu-container',
    templateUrl: './avi-main-header-user-menu-container.component.html',
})
export class AviMainHeaderUserMenuContainerComponent {
    /**
     * Return account username, ex. "admin".
     */
    public readonly username$ = this.store.select(selectUsername);

    /**
     * Return account full name, ex. "System Administrator".
     */
    public readonly fullName$ = this.store.select(UserAccount.selectFullName);

    /**
     * Return account email.
     */
    public readonly email$ = this.store.select(UserAccount.selectEmail);

    /**
     * Return customer portal URL fetched from cportalService.
     */
    public customerPortalUrl$ = this.store.select(selectDefaultLicenseTier)
        .pipe(
            take(1),
            switchMap(defaultLicenseTier => {
                return from(this.cportalService.getPortalInfo()).pipe(
                    catchError(error => {
                        this.devLoggerService.error(error);

                        return throwError(() => error);
                    }),
                    map(portalInfo => {
                        let aviPortalUrl = portalInfo.portal_url;

                        if (defaultLicenseTier !== LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
                            aviPortalUrl += PORTAL_URL_VMW_MODE;
                        }

                        return aviPortalUrl;
                    }),
                );
            }),
        );

    /**
     * Return last login time as human-readable string.
     */
    public lastLogin$ = combineLatest([
        this.store.select(selectUsername),
        this.store.select(selectControllerTimeDifference).pipe(take(1)),
    ])
        .pipe(
            switchMap(([username, controllerTimeDifference]) => {
                return from(this.loadUserActivity(username)).pipe(
                    catchError(error => throwError(() => error)),
                    map(userActivity => {
                        const lastLoginTimestamp = userActivity?.last_login_timestamp;
                        let lastLogin = '';

                        if (lastLoginTimestamp) {
                            lastLogin += moment
                                .utc(lastLoginTimestamp)
                                .add(controllerTimeDifference, 'seconds')
                                .fromNow();
                        }

                        return this.stringService.capitalize(lastLogin);
                    }),
                );
            }),
        );

    constructor(
        private readonly store: Store,
        private readonly authService: Auth,
        @Inject(UserItem)
        private readonly UserItemClass: TUserItem,
        private readonly stringService: StringService,
        private readonly cportalService: CportalService,
        private readonly devLoggerService: DevLoggerService,
    ) {}

    /**
     * Load user activity if user has permissions.
     */
    private loadUserActivity(username: string): IPromise<IUserActivity> {
        // Load useractivity only when current user has 'PERMISSION_USER' access.
        if (this.authService.isAllowed('user')) {
            return this.UserItemClass.loadUserActivity(username);
        }

        return Promise.resolve({});
    }
}
